/**
 * persistence/managers/CleanupManager.js
 *
 * Global stale-data cleanup for IndexedDB.
 *
 * Why this is needed
 * ──────────────────
 * The per-session pruners (pruneVersions, pruneDiffLog) only run when a
 * specific match is opened again.  If an analyst works on 4 matches per day
 * and never re-opens old ones, those sessions accumulate indefinitely:
 *
 *   Table          Leak source
 *   ─────────────  ───────────────────────────────────────────────────────
 *   sessions       Full currentString (~12 KB) stored per session, never deleted
 *   versions       10 checkpoints × 12 KB per session, only pruned on re-open
 *   diffLog        Up to 1,000 entries per session, only pruned on re-open
 *   firestoreQueue "done" items store the full string in payload — never cleaned
 *
 * Storage estimate without cleanup (4 matches/day, 30 days):
 *   versions       120 sessions × 10 × 12 KB = ~14 MB
 *   firestoreQueue 120 sessions × 5 items × 12 KB = ~7 MB
 *   sessions       120 × 12 KB = ~1.4 MB
 *   diffLog        120 × ~50 entries × ~200 B = ~1.2 MB
 *   Total          ~24 MB/month, unbounded growth
 *
 * Storage estimate WITH cleanup (30-day rolling window):
 *   All of the above, but capped at the rolling window — never grows past ~24 MB
 *   regardless of how long the app is used.
 *
 * Throttling
 * ──────────
 * The cleanup runs at most once every 24 hours (controlled by a localStorage
 * timestamp).  It fires on InputBoard mount — the first time the analyst opens
 * a match each calendar day.
 */

import {
  sessionsTable,
  versionsTable,
  diffLogTable,
  firestoreQueueTable,
} from "../db/database";
import {
  STALE_SESSION_MAX_AGE_MS,
  FIRESTOREQUEUE_DONE_MAX_AGE_MS,
} from "../config";

const CLEANUP_LS_KEY          = "stepout_last_db_cleanup";
const CLEANUP_MIN_INTERVAL_MS = 24 * 60 * 60_000; // at most once per 24 h

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Runs the global stale-data cleanup if it hasn't run in the last 24 hours.
 *
 * Safe to call on every InputBoard mount — the localStorage throttle makes
 * it a no-op on all but the first call of the day.
 *
 * Fire-and-forget: the caller does NOT need to await this.
 *
 * @returns {Promise<void>}
 */
export async function runStaleDataCleanup() {
  // ── Throttle ──────────────────────────────────────────────────────────────
  try {
    const lastRun = parseInt(localStorage.getItem(CLEANUP_LS_KEY) ?? "0", 10);
    if (Date.now() - lastRun < CLEANUP_MIN_INTERVAL_MS) return;
  } catch {
    // localStorage unavailable (private browsing) — run anyway
  }

  try {
    const sessionCutoff   = Date.now() - STALE_SESSION_MAX_AGE_MS;
    const firestoreCutoff = Date.now() - FIRESTOREQUEUE_DONE_MAX_AGE_MS;

    // ── 1. Delete sessions + all their associated records ──────────────────
    //
    // We query by updatedAt so a session that was last touched 30 days ago
    // is cleaned up even if it was created earlier.  updatedAt is indexed.
    const staleSessions = await sessionsTable()
      .where("updatedAt")
      .below(sessionCutoff)
      .toArray();

    let deletedSessions = 0;
    let deletedVersions = 0;
    let deletedDiffLogs = 0;
    let deletedFsItems  = 0;

    for (const session of staleSessions) {
      const pair = [session.matchId, session.analystId];

      // Delete versions for this (matchId, analystId)
      const vCount = await versionsTable()
        .where("[matchId+analystId]")
        .equals(pair)
        .delete();
      deletedVersions += vCount;

      // Delete diffLog entries for this session
      const dCount = await diffLogTable()
        .where("[matchId+analystId]")
        .equals(pair)
        .delete();
      deletedDiffLogs += dCount;

      // Delete firestoreQueue items for this session (all statuses)
      const fCount = await firestoreQueueTable()
        .where("[matchId+analystId]")
        .equals(pair)
        .delete();
      deletedFsItems += fCount;

      // Finally delete the session record itself
      await sessionsTable().delete(session.id);
      deletedSessions++;
    }

    // ── 2. Prune old firestoreQueue "done" items ───────────────────────────
    //
    // These accumulate even for recent sessions once the Firestore write
    // succeeds.  They store the full event string in their payload field,
    // so they're disproportionately large relative to row count.
    // queuedAt is indexed.
    const extraFsCount = await firestoreQueueTable()
      .where("status")
      .equals("done")
      .filter((r) => (r.queuedAt ?? 0) < firestoreCutoff)
      .delete();
    deletedFsItems += extraFsCount;

    // ── Mark completion ────────────────────────────────────────────────────
    try {
      localStorage.setItem(CLEANUP_LS_KEY, String(Date.now()));
    } catch { /* ignore */ }

    if (deletedSessions > 0 || deletedVersions > 0 || extraFsCount > 0) {
      console.log(
        `[CleanupManager] Stale data purged — sessions: ${deletedSessions}, ` +
        `versions: ${deletedVersions}, diffLog: ${deletedDiffLogs}, ` +
        `firestoreQueue: ${deletedFsItems}`
      );
    }
  } catch (error) {
    console.warn("[CleanupManager] Stale data cleanup failed:", error.message);
    // Non-fatal — the app works correctly without cleanup; storage just grows.
  }
}
